import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AppSidebar from "../../../components/common/AppSidebar";
import { getBasicUserDetailsById } from "../../../setup/api/services/user";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import { useKcToken } from "../../../setup/auth/useKcToken";
import { IUserInfoResponseData } from "../../../setup/api/services/user/models";
import {
  getOrganization,
  uploadOrganizationLogo,
} from "../../../setup/api/services/organization";
import { useOrganizationCode } from "../../../setup/util/extractOrganizationCode";

/**
 * App-shell sidebar: resolves the signed-in user and their organization, then renders the
 * shared AppSidebar. The organization gate renders that same component in `inert` mode, so
 * both chromes stay identical by construction.
 */
const Sidebar = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState<IUserInfoResponseData | undefined>(
    undefined
  );

  const userToken = useKcToken();
  const [organization, setOrganization] = useState<{
    name?: string;
    logoUrl?: string;
  } | null>(null);

  const organizationCode = useOrganizationCode(userToken) || undefined;

  useEffect(() => {
    const getUserDetails = async () => {
      const userId = AuthConfig.getUserInfo()?.id;

      const result = await getBasicUserDetailsById(userId);

      setUserInfo(result?.data);
    };

    getUserDetails();
  }, []);

  const fetchOrganization = useCallback(async () => {
    if (!organizationCode) return;
    const response = await getOrganization(organizationCode as string);
    setOrganization(response.data);
  }, [organizationCode]);

  useEffect(() => {
    if (organizationCode) {
      fetchOrganization();
    }
  }, [fetchOrganization, organizationCode]);

  const onLogoChange = useCallback(
    async (file: File) => {
      if (!organizationCode) return;
      const logoUrl = await uploadOrganizationLogo(organizationCode, file);
      // Reflect it immediately rather than refetching the organization.
      setOrganization((prev) => ({ ...prev, logoUrl }));
    },
    [organizationCode]
  );

  return (
    <AppSidebar
      organizationName={organization?.name}
      organizationLogoUrl={organization?.logoUrl}
      brandSubLabel={t("lbl_company") as string}
      userName={userInfo?.profile?.fullName}
      userRoleLabel={t("lbl_executive") as string}
      userPhotoUrl={userInfo?.profile?.photoUrl}
      onLogoChange={organizationCode ? onLogoChange : undefined}
    />
  );
};

export default Sidebar;
